import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';

/*
  Minimal typed shapes based on your backend output.
  Adjust/add fields as necessary.
*/
export interface FirestoreTimestamp {
  _seconds: number;
  _nanoseconds: number;
}

export interface BaseListing {
  id: string;
  userId?: string;
  cardId?: string;
  type: string;
  createdAt?: FirestoreTimestamp | Date | string;
  updatedAt?: FirestoreTimestamp | Date | string;
  [key: string]: any;
}

export interface ProductListing extends BaseListing {
  type: 'product';
  name?: string;
  description?: string;
  price?: string;
  currency?: string;
  imageUrl?: string[]; // backend uses imageUrl (array) on products
  features?: string[];
}

export interface EventListing extends BaseListing {
  type: 'events';
  title?: string;
  description?: string;
  price?: string;
  imageUrls?: string[];
  features?: string[];
}

export interface CharityListing extends BaseListing {
  type: 'charity';
  title?: string;
  imageUrl?: string | null; // sometimes single string
  imageUrls?: string[];     // sometimes array
}

export interface ServiceListing extends BaseListing {
  type: 'service';
  title?: string;
  description?: string;
  imageUrls?: string[];
  price?: string;
  currency?: string;
  features?: string[];
}
export interface ProjectListing extends BaseListing {
  type: 'projects';
  title?: string;
  description?: string;
  imageUrls?: string[];
  price?: string;
  currency?: string;
  projectType?: string;
  status?: string;
  amenities?: string[];
  YoutubeId?: string;
}

export interface ReviewListing extends BaseListing {
  type: 'reviews';
  title?: string;
  review?: string;
  name?: string;
}

// ✅ NEW: Match your backend response structure


export interface ListingResponse {
  product?: ProductListing[];
  events?: EventListing[];
  charity?: CharityListing[];
  service?: ServiceListing[];
  reviews?: ReviewListing[];
  // allow other keys if backend adds types
  [key: string]: BaseListing[] | undefined;
}

@Injectable({
  providedIn: 'root'
})
export class ListingService {
  private readonly baseUrl = environment.apiUrl;

  // State subjects
  public listingsSubject = new BehaviorSubject<ListingResponse | null>(null);
  public listings$ = this.listingsSubject.asObservable();

  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  private errorSubject = new BehaviorSubject<string | null>(null);
  public error$ = this.errorSubject.asObservable();

  constructor(private http: HttpClient, private authService: AuthService) {}

  // --- Helpers ---
  private getAuthHeaders(): HttpHeaders {
    const token = (this.authService as any).idToken;
    if (!token) {
      throw new Error('User not authenticated');
    }
    return new HttpHeaders({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' });
  }

  private normalizeListingResponse(raw: ListingResponse | null | undefined): ListingResponse {
    const base: ListingResponse = {
      product: [],
      events: [],
      charity: [],
      service: [],
      reviews: []
    };
    if (!raw) return base;
    return {
      product: Array.isArray(raw.product) ? raw.product : [],
      events: Array.isArray(raw.events) ? raw.events : [],
      charity: Array.isArray(raw.charity) ? raw.charity : [],
      service: Array.isArray(raw.service) ? raw.service : [],
      reviews: Array.isArray(raw.reviews) ? raw.reviews : [],
      ...Object.keys(raw).reduce((acc: any, k) => {
        if (!['product','events','charity','service','reviews'].includes(k)) {
          acc[k] = Array.isArray((raw as any)[k]) ? (raw as any)[k] : [];
        }
        return acc;
      }, {})
    };
  }

  private setError(err: any) {
    const message = err?.error?.error || err?.message || 'Unknown error';
    this.errorSubject.next(String(message));
  }

  // Find and update one item locally (no server call)
  private patchLocalListing(listingId: string, update: Partial<BaseListing>) {
    const current = this.listingsSubject.value;
    if (!current) return;
    const next = { ...current };
    Object.keys(next).forEach(key => {
      const arr = (next as any)[key] as BaseListing[] | undefined;
      if (!Array.isArray(arr)) return;
      const idx = arr.findIndex(i => i.id === listingId);
      if (idx >= 0) {
        const item = { ...arr[idx], ...update, updatedAt: update.updatedAt ?? { _seconds: Math.floor(Date.now() / 1000), _nanoseconds: 0 } };
        const newArr = arr.slice();
        newArr[idx] = item;
        (next as any)[key] = newArr;
      }
    });
    this.listingsSubject.next(next);
  }

  private removeLocalListing(listingId: string) {
    const current = this.listingsSubject.value;
    if (!current) return;
    const next = { ...current };
    Object.keys(next).forEach(key => {
      const arr = (next as any)[key] as BaseListing[] | undefined;
      if (!Array.isArray(arr)) return;
      const newArr = arr.filter(i => i.id !== listingId);
      (next as any)[key] = newArr;
    });
    this.listingsSubject.next(next);
  }

  // --- Public API ---
  /**
   * Trigger a fetch for all listing types for given cardId.
   * This updates listings$, loading$ and error$.
   */
  fetchListings(cardId: string): void {
  if (!cardId) {
    this.errorSubject.next('cardId is required');
    return;
  }

  this.loadingSubject.next(true);
  this.errorSubject.next(null);

  const headers = this.getAuthHeaders();

  console.log('[DEBUG] ListingService: Making request to:', `${this.baseUrl}/listing/${cardId}`);

  this.http.get<ListingResponse>(`${this.baseUrl}/listing/${cardId}`, { headers }) // 👈 Changed here
    .pipe(
      map(res => {
        console.log('[DEBUG] ListingService: Raw response (direct ListingResponse):', res); // 👈 Now correct
        return this.normalizeListingResponse(res);
      }),
      tap(res => {
        console.log('[DEBUG] ListingService: Normalized products data:', res.product); // 👈 Now will show [5 items]
        this.listingsSubject.next(res);
      }),
      catchError(err => {
        console.error('[DEBUG] ListingService: HTTP ERROR:', err);
        this.setError(err);
        return of(null);
      }),
      finalize(() => {
        console.log('[DEBUG] ListingService: Request completed (loading = false)');
        this.loadingSubject.next(false);
      })
    ).subscribe();
}

  /**
   * Returns an observable that performs a single fetch (useful if you want direct observable)
   */
  getListingsOnce(cardId: string): Observable<ListingResponse | null> {
    if (!cardId) return of(null);
    const headers = this.getAuthHeaders();
    this.loadingSubject.next(true);
    this.errorSubject.next(null);

    return this.http.get<ListingResponse>(`${this.baseUrl}/listing/${cardId}`, { headers })
  .pipe(
    map(res => this.normalizeListingResponse(res)),// ✅ Extract .data
        tap(res => this.listingsSubject.next(res)),
        catchError(err => {
          this.setError(err);
          return of(null);
        }),
        finalize(() => this.loadingSubject.next(false))
      );
  }

  /**
   * Create listing under given cardId.
   * By default this will re-fetch the whole listing for the card once server returns success.
   * If you prefer optimistic update, pass reloadAfter=false (we will attempt a local append).
   */
  createListing(cardId: string, data: any, reloadAfter = true): Observable<{ message: string; id: string }> {
    if (!cardId) return throwError(() => new Error('cardId is required'));
    const headers = this.getAuthHeaders();
    this.loadingSubject.next(true);
    this.errorSubject.next(null);

    return this.http.post<{ message: string; id: string }>(
      `${this.baseUrl}/listing/${cardId}`,
      data,
      { headers }
    ).pipe(
      tap(resp => {
        if (reloadAfter) {
          this.fetchListings(cardId);
        } else {
          // attempt local append if 'type' is present in data
          const type = data?.type;
          if (type) {
            const cur = this.normalizeListingResponse(this.listingsSubject.value || {});
            const arr = Array.isArray((cur as any)[type]) ? (cur as any)[type].slice() : [];
            const newItem = { id: resp.id, cardId, ...data, createdAt: { _seconds: Math.floor(Date.now() / 1000), _nanoseconds: 0 }, updatedAt: { _seconds: Math.floor(Date.now() / 1000), _nanoseconds: 0 } };
            arr.unshift(newItem);
            (cur as any)[type] = arr;
            this.listingsSubject.next(cur);
          } else {
            // fallback: reload
            this.fetchListings(cardId);
          }
        }
      }),
      catchError(err => {
        this.setError(err);
        return throwError(() => err);
      }),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Update a listing by its listing id.
   * If cardId is provided we'll re-fetch the card listings; otherwise we patch local state.
   */
  updateListing(listingId: string, data: Partial<BaseListing>, cardId?: string): Observable<any> {
    if (!listingId) return throwError(() => new Error('id is required'));
    const headers = this.getAuthHeaders();
    this.loadingSubject.next(true);
    this.errorSubject.next(null);

    return this.http.put<{ message: string; id: string }>(`${this.baseUrl}/listing/${listingId}`, data, { headers }).pipe(
      tap(() => {
        if (cardId) {
          this.fetchListings(cardId);
        } else {
          this.patchLocalListing(listingId, data);
        }
      }),
      catchError(err => {
        this.setError(err);
        return throwError(() => err);
      }),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Delete a listing by its listing id.
   * If cardId is provided we'll re-fetch; otherwise we remove from local state.
   */
  deleteListing(listingId: string, cardId?: string): Observable<any> {
    if (!listingId) return throwError(() => new Error('id is required'));
    const headers = this.getAuthHeaders();
    this.loadingSubject.next(true);
    this.errorSubject.next(null);

    return this.http.delete<{ message: string }>(`${this.baseUrl}/listing/${listingId}`, { headers }).pipe(
      tap(() => {
        if (cardId) {
          this.fetchListings(cardId);
        } else {
          this.removeLocalListing(listingId);
        }
      }),
      catchError(err => {
        this.setError(err);
        return throwError(() => err);
      }),
      finalize(() => this.loadingSubject.next(false))
    );
  }

  // --- Convenience selectors ---

  get products$(): Observable<ProductListing[]> {
    return this.listings$.pipe(map(l => (l?.product ?? []) as ProductListing[]));
  }

  get events$(): Observable<EventListing[]> {
    return this.listings$.pipe(map(l => (l?.events ?? []) as EventListing[]));
  }

  get charity$(): Observable<CharityListing[]> {
    return this.listings$.pipe(map(l => (l?.charity ?? []) as CharityListing[]));
  }

  get services$(): Observable<ServiceListing[]> {
    return this.listings$.pipe(map(l => (l?.service ?? []) as ServiceListing[]));
  }

  get reviews$(): Observable<ReviewListing[]> {
    return this.listings$.pipe(map(l => (l?.reviews ?? []) as ReviewListing[]));
  }

  public projects$: Observable<ProjectListing[]> = this.listings$.pipe(
    map(listings => (listings && listings['projects']) ? listings['projects'] as ProjectListing[] : [])
  );

  /** Clear local state (doesn't touch backend) */
  clearState() {
    this.listingsSubject.next(null);
    this.errorSubject.next(null);
    this.loadingSubject.next(false);
  }
}